// @ts-nocheck
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useOutletContext, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/clerkShim';
import _ from 'lodash';
import { toast } from 'sonner';
import helpers from '@/utils/helpers';

// Store imports
import useTimeSlotStore from '@store/timeSlotStore';
import useGameStore from '@store/gameStore';
import useDivisionStore from '@store/divisionStore';
import useTeamDivisionStore from '@store/teamDivisionStore';
import useLeagueStore from '@store/leagueStore';
import useTournamentStore from '@store/tournamentStore';
import { isParseableTime, toDateKey as toGameDateKey } from '@staty/shared/utils/gameDateTime';
import QuickAvailabilityWizard from '@/components/QuickAvailabilityWizard/QuickAvailabilityWizard';

// UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Calendar, CalendarDays, Plus, Save } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";

// Shared Components and Utilities
import { 
  DraggableTeam, 
  SlotSection, 
  GameSlot,
  timeToMinutes,
  minutesToTime,
  formatTimeForDisplay,
  isTimeInRange,
  findMatchingTimeSlot,
  processGamesForTimeSlotMatching,
  getGamesForTimeAndDate,
  filterGamesByDivisions,
  calculateSharedSlotSections,
  getTimeSlotsForTimeAndDate
} from './shared';
import getLocationDisplayName from '@staty/shared/utils/getLocationDisplayName';
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';

interface Team {
  _id: string;
  name: string;
  division: string;
}

interface Game {
  _id?: string;
  timeSlot?: string;
  homeTeam?: string | Team;
  awayTeam?: string | Team;
  division: string;
  date?: string;
  globalDate?: string; // MM-DD-YYYY format
  time: string;
  location?: string;
  locationModel?: string;
  slotSectionId?: string; // Track which slot section this game belongs to
  duration?: number; // Duration in minutes
}

interface TimeSlot {
  _id: string;
  location: {
    _id: string;
    name: string;
  };
  availableDate: string;
  startTime: string;
  endTime: string;
  maxGames: number;
  timeZone: string;
}

interface Division {
  _id: string;
  name: string;
  sports: string[];
  config?: {
    gameDurationInMinutes?: number;
  };
}
interface SlotSection {
  id: string;
  timeSlotId: string;
  game?: Game;
  divisionId: string;
  slotTime: string; // Calculated time for this specific slot
  duration?: number; // Duration in minutes for this slot section
}


interface Season {
  _id: string;
  name: string;
  tournament?: string;
}


export default function SchedulerAdmin() {
  const { tournamentId, leagueId } = useParams();
  const { leagueName, league: organization } = useOutletContext() as any;
  const { getToken } = useAuth();


  // State
  const [selectedLeagueId, setSelectedLeagueId] = useState<string>('');
  const [selectedDivisionIds, setSelectedDivisionIds] = useState<string[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [gamesByDate, setGamesByDate] = useState<Record<string, Game[]>>({});
  const [pendingGames, setPendingGames] = useState<{
    toCreate: Omit<Game, '_id'>[];
    toUpdate: Game[];
    toDelete: string[];
  }>({
    toCreate: [],
    toUpdate: [],
    toDelete: []
  });

  // Store hooks
  const { timeSlots, getTimeSlotsByLeagueName, getTimeSlotsByLeagueId } = useTimeSlotStore();
  const { games, addGame, updateGame, deleteGame, getGamesBySeasonId } = useGameStore((state: any) => ({
    games: state.games,
    addGame: state.addGame(getToken),
    updateGame: state.updateGame(getToken),
    deleteGame: state.deleteGame(getToken),
    getGamesBySeasonId: state.getGamesBySeasonId,
  }));
  const { divisions, getDivisionsByLeagueId, getActiveDivisionsByTournamentId } = useDivisionStore();
  // INT-0027: default to ALL divisions selected — a fresh visit used to
  // render an empty grid until the admin found the division checkboxes.
  const autoSelectedRef = React.useRef(false);
  React.useEffect(() => {
    if (autoSelectedRef.current) return;
    if (!divisions || divisions.length === 0) return;
    autoSelectedRef.current = true;
    setSelectedDivisionIds(divisions.map((d: any) => d._id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divisions]);
  const { teamDivisions, getTeamDivisionsByDivisionId } = useTeamDivisionStore();
  const { seasons, getContestsByTournamentId } = useLeagueStore((state: any) => ({
    seasons: state.leagues,
    getContestsByTournamentId: state.getContestsByTournamentId,
  }));
  const { tournaments } = useTournamentStore();

  // Sensors for drag and drop with optimized configuration
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Add activation constraints to prevent immediate triggering on trackpad
      activationConstraint: {
        distance: 8, // Require 8px movement before drag starts
        delay: 100, // 100ms delay to prevent accidental drags
        tolerance: 5, // Allow 5px tolerance for small movements
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Set selectedLeagueId from URL parameter
  useEffect(() => {
    if (leagueId) {
      setSelectedLeagueId(leagueId);
    }
  }, [leagueId]);

  // Load initial data
  useEffect(() => {
    if (leagueId) {
      getTimeSlotsByLeagueId(leagueId);
    } else if (leagueName) {
      getTimeSlotsByLeagueName(leagueName);
    }
  }, [leagueId, leagueName, getTimeSlotsByLeagueId, getTimeSlotsByLeagueName]);
        
  // Load seasons/contests for tournaments
  useEffect(() => {
        if (tournamentId) {
      getContestsByTournamentId(tournamentId);
    }
  }, [tournamentId, getContestsByTournamentId]);

  // Load divisions when season is selected
  useEffect(() => {
    console.log('Loading divisions for selectedLeagueId:', selectedLeagueId, 'tournamentId:', tournamentId);
    if (selectedLeagueId) {
      if (tournamentId) {
        console.log('Loading divisions by tournament ID:', tournamentId);
        getActiveDivisionsByTournamentId(tournamentId);
      } else {
        console.log('Loading divisions by season ID:', selectedLeagueId);
        getDivisionsByLeagueId(selectedLeagueId);
      }
    }
  }, [selectedLeagueId, tournamentId, getDivisionsByLeagueId, getActiveDivisionsByTournamentId]);

  // Load teams when divisions are selected
  useEffect(() => {
    if (selectedDivisionIds.length > 0) {
      selectedDivisionIds.forEach(divisionId => {
        getTeamDivisionsByDivisionId(divisionId);
      });
    }
  }, [selectedDivisionIds, getTeamDivisionsByDivisionId]);

  // Load games when season is selected
  useEffect(() => {
    if (selectedLeagueId) {
      getGamesBySeasonId(selectedLeagueId);
    }
  }, [selectedLeagueId, getGamesBySeasonId]);

  // Load games when divisions are selected
  useEffect(() => {
    if (selectedDivisionIds.length > 0) {
      // Clear existing games first
      setGamesByDate({});
      
      // Load games for all selected divisions efficiently
      const { getGamesByDivisionIds } = useGameStore.getState();
      getGamesByDivisionIds(selectedDivisionIds);
    } else {
      setGamesByDate({});
    }
  }, [selectedDivisionIds]);

  // Group games by date when games are loaded
  useEffect(() => {
    if (games.length > 0 && selectedDivisionIds.length > 0) {
      const filteredGames = games.filter(game => {
        const gameDivisionId = helpers.getObjId(game.division);
        const isSelected = selectedDivisionIds.includes(gameDivisionId);
        return isSelected;
      });

      // Process games for timeSlot matching
      const processedGames = processGamesForTimeSlotMatching(filteredGames, timeSlots);

      // Group games by globalDate
      const grouped = _.groupBy(processedGames, 'globalDate');
      setGamesByDate(grouped);
    } else {
      setGamesByDate({});
    }
  }, [games, selectedDivisionIds, timeSlots]);



  // Cleanup: Re-enable scrolling if component unmounts during drag
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
      document.body.style.userSelect = '';
    };
  }, []);

  // Debug divisions loading
  useEffect(() => {
    console.log('Divisions loaded:', divisions.length, divisions);
  }, [divisions]);

  // Filter teams by selected divisions
  const filteredTeams = useMemo(() => {
    return teamDivisions.filter(team => 
      selectedDivisionIds.includes(helpers.getObjId(team.division))
    );
  }, [teamDivisions, selectedDivisionIds]);

  // Group teams by division
  const teamsByDivision = useMemo(() => {
    return _.groupBy(filteredTeams, (team) => helpers.getObjId(team.division));
  }, [filteredTeams]);

  // Group timeSlots by availableDate first, then by location, sorted by startTime
  const timeSlotsByDate = useMemo(() => {
    const grouped: Record<string, Record<string, TimeSlot[]>> = {};
    
    // First group by date
    timeSlots.forEach(slot => {
      if (!grouped[slot.availableDate]) {
        grouped[slot.availableDate] = {};
      }
      if (!grouped[slot.availableDate][slot.location._id]) {
        grouped[slot.availableDate][slot.location._id] = [];
      }
      grouped[slot.availableDate][slot.location._id].push(slot);
      });
      
      // Sort each location group by startTime
    Object.keys(grouped).forEach(date => {
      Object.keys(grouped[date]).forEach(locationId => {
        grouped[date][locationId].sort((a, b) => a.startTime.localeCompare(b.startTime));
      });
    });
    
    // Sort dates chronologically and create a new ordered object
    const unsortedDates = Object.keys(grouped);
    const sortedDates = unsortedDates.sort((a, b) => {
      // Handle MM-DD-YYYY format
      let dateA, dateB;
      if (a.includes('-') && a.length === 10) {
        const [monthA, dayA, yearA] = a.split('-');
        dateA = new Date(parseInt(yearA), parseInt(monthA) - 1, parseInt(dayA)).getTime();
      } else {
        dateA = new Date(a).getTime();
      }
      
      if (b.includes('-') && b.length === 10) {
        const [monthB, dayB, yearB] = b.split('-');
        dateB = new Date(parseInt(yearB), parseInt(monthB) - 1, parseInt(dayB)).getTime();
      } else {
        dateB = new Date(b).getTime();
      }
      
      return dateA - dateB;
    });
    
    const sortedGrouped: Record<string, Record<string, TimeSlot[]>> = {};
    sortedDates.forEach(date => {
      // Sort locations by their earliest startTime within this date
      const locationIds = Object.keys(grouped[date]);
      const sortedLocationIds = locationIds.sort((a, b) => {
        const timesA = grouped[date][a].map(slot => slot.startTime).sort();
        const timesB = grouped[date][b].map(slot => slot.startTime).sort();
        const earliestTimeA = timesA[0]; // First (earliest) time
        const earliestTimeB = timesB[0]; // First (earliest) time
        return earliestTimeA.localeCompare(earliestTimeB);
      });
      
      // Create sorted object for this date
      const sortedDateGroup: Record<string, TimeSlot[]> = {};
      sortedLocationIds.forEach(locationId => {
        sortedDateGroup[locationId] = grouped[date][locationId];
      });
      
      sortedGrouped[date] = sortedDateGroup;
    });
    
    
    return sortedGrouped;
  }, [timeSlots]);

  // Generate time slots for the grid Y-axis
  const generateTimeSlots = useMemo(() => {
    const allTimes = new Set<number>();
    
    // Collect all times from timeSlots
    timeSlots.forEach(timeSlot => {
      allTimes.add(timeToMinutes(timeSlot.startTime));
      allTimes.add(timeToMinutes(timeSlot.endTime));
    });
    
    // Collect all times from games
    Object.values(gamesByDate).flat().forEach(game => {
      if (game.time) {
        allTimes.add(timeToMinutes(game.time));
      }
    });
    
    // Convert to array and sort
    const sortedTimes = Array.from(allTimes).sort((a, b) => a - b);
    
    // Generate time slots (every 30 minutes from earliest to latest)
    const generatedTimeSlots = [];
    if (sortedTimes.length > 0) {
      const startTime = Math.floor(sortedTimes[0] / 30) * 30; // Round down to nearest 30 min
      const endTime = Math.ceil(sortedTimes[sortedTimes.length - 1] / 30) * 30; // Round up to nearest 30 min
      
      for (let time = startTime; time <= endTime; time += 30) {
        generatedTimeSlots.push({
          time: minutesToTime(time),
          minutes: time,
          displayTime: formatTimeForDisplay(minutesToTime(time))
        });
      }
    }
    
    return generatedTimeSlots;
  }, [timeSlots, gamesByDate]);

  // Generate dates for the grid X-axis
  const generateDates = useMemo(() => {
    const timeSlotDates = Object.keys(timeSlotsByDate);
    const gameDates = Object.keys(gamesByDate);
    const allDates = [...new Set([...timeSlotDates, ...gameDates])];
    
    // Sort dates chronologically
    const sortedDates = allDates.sort((a, b) => {
      // Handle MM-DD-YYYY format
      let dateA, dateB;
      if (a.includes('-') && a.length === 10) {
        const [monthA, dayA, yearA] = a.split('-');
        dateA = new Date(parseInt(yearA), parseInt(monthA) - 1, parseInt(dayA)).getTime();
      } else {
        dateA = new Date(a).getTime();
      }
      
      if (b.includes('-') && b.length === 10) {
        const [monthB, dayB, yearB] = b.split('-');
        dateB = new Date(parseInt(yearB), parseInt(monthB) - 1, parseInt(dayB)).getTime();
      } else {
        dateB = new Date(b).getTime();
      }
      
      return dateA - dateB;
    });
    
    return sortedDates.slice(0, 7); // Limit to 7 dates for grid
  }, [timeSlotsByDate, gamesByDate]);



  // Get unique locations
  const locations = useMemo(() => {
    const locationMap = new Map<string, TimeSlot['location']>();
    timeSlots.forEach(slot => {
      locationMap.set(slot.location._id, slot.location);
    });
    return Array.from(locationMap.values());
  }, [timeSlots]);

  // Filter games by selected divisions
  const filteredGames = useMemo(() => {
    return filterGamesByDivisions(games, selectedDivisionIds);
  }, [games, selectedDivisionIds]);

  // INT-0027 quick availability: the drafted games (INT-0043) have dates but
  // no time; the wizard creates the venue slots and gives them times.
  const [searchParams, setSearchParams] = useSearchParams();
  const [quickOpen, setQuickOpen] = useState(false);
  const timelessGames = useMemo(
    () => (games as any[]).filter((g) => !isParseableTime(g?.time)).map((g) => ({
      id: String(g._id), dateKey: toGameDateKey(g.globalDate || g.date), round: g?.competitionMeta?.round, gameNumber: g?.competitionMeta?.gameNumber,
    })),
    [games],
  );
  useEffect(() => {
    if (searchParams.get('quick') === '1') {
      setQuickOpen(true);
      const next = new URLSearchParams(searchParams); next.delete('quick'); setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const quickCompetitionId = leagueId || selectedLeagueId;
  const quickCompetition = quickCompetitionId
    ? { kind: 'league' as const, id: quickCompetitionId, name: (seasons.find((x: any) => x._id === quickCompetitionId)?.name) || 'This season' }
    : null;
  const onQuickDone = () => {
    if (leagueId) getTimeSlotsByLeagueId(leagueId); else if (leagueName) getTimeSlotsByLeagueName(leagueName);
    if (selectedLeagueId) getGamesBySeasonId(selectedLeagueId);
  };

  // Merge pending changes with current games for display
  const displayGames = useMemo(() => {
    
    let mergedGames: Game[] = [...filteredGames];
    
    // Helper to normalize homeTeam/awayTeam to strings
    const normalizeGame = (game: Game): Game => {
      return {
        ...game,
        homeTeam: typeof game.homeTeam === 'object' && game.homeTeam ? helpers.getObjId(game.homeTeam) : (game.homeTeam as string | undefined),
        awayTeam: typeof game.awayTeam === 'object' && game.awayTeam ? helpers.getObjId(game.awayTeam) : (game.awayTeam as string | undefined),
      };
    };
    
    // Apply pending updates
    pendingGames.toUpdate.forEach(pendingGame => {
      const normalizedGame = normalizeGame(pendingGame);
      const index = mergedGames.findIndex(g => g._id === normalizedGame._id);
      if (index !== -1) {
        mergedGames[index] = normalizedGame as any;
      } else {
        // If not found in existing games, it might be a temporary game
        // Add it to the merged games
        mergedGames.push(normalizedGame as any);
      }
    });
    
    // Add pending creates (with stable temporary IDs for display)
    pendingGames.toCreate.forEach((pendingGame, index) => {
      const normalizedGame = normalizeGame(pendingGame);
      const tempId = `temp-create-${normalizedGame.slotSectionId || index}`;
      mergedGames.push({
        ...normalizedGame,
        _id: tempId // Use slotSectionId for unique IDs
      } as any);
    });
    
    // Remove pending deletes
    mergedGames = mergedGames.filter(g => {
      const shouldDelete = pendingGames.toDelete.includes(g._id);
      if (shouldDelete) {
      }
      return !shouldDelete;
    });
    
    
    return mergedGames;
  }, [filteredGames, pendingGames]);

  // Calculate shared slot sections for a timeSlot (not per division) - memoized for performance
  const calculateSharedSlotSectionsCallback = useCallback((timeSlot: TimeSlot) => {
    return calculateSharedSlotSections(timeSlot, displayGames as any, divisions, selectedDivisionIds, pendingGames as any, organization?.sports?.[0]);
  }, [displayGames, divisions, selectedDivisionIds, pendingGames, organization]);

  // Helper function to get games for a specific time and date from processed games
  const getGamesForTimeAndDateCallback = useCallback((timeSlot: string, date: string) => {
    // Filter displayGames by date and time
    const gamesForDate = displayGames.filter(game => game.globalDate === date);
    const timeMinutes = timeToMinutes(timeSlot);

    // Find games that match this exact time (within 15 minutes tolerance)
    const matchingGames = gamesForDate.filter(game => {
      if (!game.time) return false;
      const gameTimeMinutes = timeToMinutes(game.time);
      return Math.abs(gameTimeMinutes - timeMinutes) <= 15; // Within 15 minutes
    });

    return matchingGames;
  }, [displayGames]);

  // Helper function to get timeSlots for a specific time and date
  const getTimeSlotsForTimeAndDateCallback = useCallback((timeSlot: string, date: string) => {
    return getTimeSlotsForTimeAndDate(timeSlotsByDate, timeSlot, date, calculateSharedSlotSectionsCallback);
  }, [timeSlotsByDate, calculateSharedSlotSectionsCallback]);

  const handleDragStart = useMemo(() => (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    
    // Disable page scrolling during drag
    document.body.style.overflow = 'hidden';
    document.body.style.userSelect = 'none';
  }, []);

  const handleDragEnd = useMemo(() => (event: DragEndEvent) => {
    const { active, over } = event;
    
    setActiveId(null);
    
    // Re-enable page scrolling after drag
    document.body.style.overflow = '';
    document.body.style.userSelect = '';

    if (!over) {
      return;
    }

    // Handle dropping teams into team slots
    const team = filteredTeams.find(t => t._id === active.id);
    
    if (team && over.id.toString().includes('-')) {
      // Parse the droppable ID: slotId-teamType (e.g., "slotId-homeTeam")
      const overIdParts = over.id.toString().split('-');
      const teamType = overIdParts[overIdParts.length - 1] as 'homeTeam' | 'awayTeam';
      const slotId = overIdParts.slice(0, -1).join('-');
      
      // Parse slotId to get timeSlotId and sectionIndex
      const slotIdParts = slotId.split('-');
      const timeSlotId = slotIdParts[0];
      const sectionIndex = parseInt(slotIdParts[1]);
      
      const timeSlot = timeSlots.find(ts => ts._id === timeSlotId);
      if (!timeSlot) {
        return;
      }
      
      const slotSections = calculateSharedSlotSectionsCallback(timeSlot);
      const slotSection = slotSections[sectionIndex];
      
      if (slotSection) {
        // Check if there's already a pending game for this slot section
        const existingPendingGame = pendingGames.toCreate.find(g => g.slotSectionId === slotSection.id);
        
        if (existingPendingGame) {
          // Update existing pending game
          const updatedGame = {
            ...existingPendingGame,
            [teamType]: team._id,
            division: helpers.getObjId(team.division),
          };
          
          setPendingGames(prev => ({
            ...prev,
            toCreate: prev.toCreate.map(g => 
              g.slotSectionId === slotSection.id ? updatedGame : g
            )
          }));
          setHasUnsavedChanges(true);
          
          toast.success(`Added ${team.name} as ${teamType === 'homeTeam' ? 'home' : 'away'} team`);
        } else if (slotSection.game) {
          // Update existing saved game
          const updatedGame = {
            ...slotSection.game,
            [teamType]: team._id,
            division: helpers.getObjId(team.division),
          };
          
          setPendingGames(prev => ({
            ...prev,
            toUpdate: [...prev.toUpdate.filter(g => g._id !== updatedGame._id), updatedGame]
          }));
          setHasUnsavedChanges(true);
          
          toast.success(`Added ${team.name} as ${teamType === 'homeTeam' ? 'home' : 'away'} team`);
        } else {
          // Create new game
          const newGame: Omit<Game, '_id'> = {
            timeSlot: slotSection.timeSlotId,
            homeTeam: teamType === 'homeTeam' ? team._id : null,
            awayTeam: teamType === 'awayTeam' ? team._id : null,
            division: helpers.getObjId(team.division),
            date: timeSlot.availableDate,
            globalDate: timeSlot.availableDate,
            time: formatTimeForDisplay(slotSection.slotTime),
            location: getLocationDisplayName(timeSlot.location),
            locationModel: timeSlot.location._id,
            slotSectionId: slotSection.id,
          };
          
          setPendingGames(prev => ({
            ...prev,
            toCreate: [...prev.toCreate, newGame]
          }));
          setHasUnsavedChanges(true);
          
          toast.success(`Created game with ${team.name} as ${teamType === 'homeTeam' ? 'home' : 'away'} team`);
        }
      }
    }
  }, [filteredTeams, timeSlots, divisions, calculateSharedSlotSectionsCallback, pendingGames]);

  const handleSaveChanges = async () => {
    try {
      // Save all pending changes
      for (const game of pendingGames.toCreate) {
        const currentDivision = divisions.find(d => d._id === game.division);
        const currentTimeSlot = timeSlots.find(ts => ts._id === game.timeSlot);
        let createdGame = {
          ...game,
          timeSlot: currentTimeSlot?._id,
          locationModel: helpers.getObjId(currentTimeSlot?.location),
          division: currentDivision?._id,
          season: helpers.getObjId(currentDivision?.season),
          league: helpers.getObjId(currentDivision?.league),
        };
        await addGame(createdGame);
      }
      
      for (const game of pendingGames.toUpdate) {
        const currentDivision = divisions.find(d => d._id === game.division);
        const currentTimeSlot = timeSlots.find(ts => ts._id === game.timeSlot);
        let updatedGame = {
          ...game,
          timeSlot: currentTimeSlot?._id,
          locationModel: helpers.getObjId(currentTimeSlot?.location),
          division: currentDivision?._id,
          season: helpers.getObjId(currentDivision?.season),
          league: helpers.getObjId(currentDivision?.league),
        };
        await updateGame(updatedGame);
      }
      
      for (const gameId of pendingGames.toDelete) {
        await deleteGame(gameId);
      }
      
      // Clear pending changes
      setPendingGames({
        toCreate: [],
        toUpdate: [],
        toDelete: []
      });
      
      toast.success('Changes saved successfully!');
      setHasUnsavedChanges(false);
    } catch (error) {
      toast.error('Failed to save changes');
      console.error('Save error:', error);
    }
  };

  const handleResetChanges = () => {
    const confirmed = window.confirm(
      'Are you sure you want to reset all changes? This will discard all unsaved games, updates, and deletions. This action cannot be undone.'
    );
    
    if (confirmed) {
      setPendingGames({
        toCreate: [],
        toUpdate: [],
        toDelete: []
      });
      setHasUnsavedChanges(false);
      toast.info('Changes reset');
    }
  };


  const activeTeam = activeId ? filteredTeams.find(t => t._id === activeId) : null;

  return (
    <PageContainer fullWidth>
      {/* Header */}
      {/* Safe to wrap since `.page-enter` uses fill-mode `backwards` — the
          transform clears after the mount animation, so the fixed "Teams by
          Division" palette and dnd-kit's DragOverlay anchor to the viewport
          by the time a drag can start. */}
      <PageHeader
        title="Scheduler"
        description={`${tournamentId ? 'Tournament' : 'Season'} Scheduler`}
      />

      {/* Selection Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Select Divisions</CardTitle>
          <CardDescription>
            Choose the divisions you want to schedule games for
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Season/Contest Selection for Tournaments */}
      {tournamentId && (
            <div className="space-y-2">
              <Label>Select Season/Contest</Label>
              <Select value={selectedLeagueId} onValueChange={setSelectedLeagueId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a season/contest" />
                </SelectTrigger>
                <SelectContent>
                  {seasons.map((season) => (
                    <SelectItem key={season._id} value={season._id}>
                      {season.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
      )}

      {/* Division Selection */}
          <div className="space-y-2">
            <Label>Select Divisions</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {divisions.map((division) => (
                <div key={division._id} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={division._id}
                    checked={selectedDivisionIds.includes(division._id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedDivisionIds([...selectedDivisionIds, division._id]);
                      } else {
                        setSelectedDivisionIds(selectedDivisionIds.filter(id => id !== division._id));
                      }
                    }}
                    className="rounded border-input"
                  />
                  <label htmlFor={division._id} className="text-sm font-medium">
                    {division.name}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Teams by Division and Calendar View - Always wrapped in DndContext */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {/* Teams by Division - Only show when divisions are selected */}
        {selectedDivisionIds.length > 0 && (
          <Card className="fixed top-4 left-6 right-6 z-50 shadow-xl border-2 border-primary/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Teams by Division</CardTitle>
              <CardDescription className="text-sm">
                Drag teams from here to the time slots below to create games
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {selectedDivisionIds.map((divisionId) => {
                  const division = divisions.find(d => d._id === divisionId);
                  const teams = teamsByDivision[divisionId] || [];
                  
                  return (
                    <div key={divisionId} className="space-y-2">
                      <h3 className="font-medium text-sm text-foreground">
                        {division?.name} ({teams.length} teams)
                      </h3>
                      <SortableContext items={teams.map(t => t._id)} strategy={verticalListSortingStrategy}>
                        <div className="grid grid-cols-2 gap-2 min-h-[100px] p-2 border rounded-lg">
                          {teams.map((team) => (
                            <DraggableTeam key={team._id} team={team} />
                          ))}
                        </div>
                      </SortableContext>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {quickCompetition && (
          <QuickAvailabilityWizard
            open={quickOpen}
            onOpenChange={setQuickOpen}
            organization={organization}
            competition={quickCompetition}
            timelessGames={timelessGames}
            gameDurationInMinutes={(divisions as any[])?.find((d: any) => d?.config?.gameDurationInMinutes)?.config?.gameDurationInMinutes || null}
            onDone={onQuickDone}
          />
        )}

        {/* Calendar View - Always visible */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Schedule Calendar
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  (grouped by date, max 5 columns)
                </span>
              </CardTitle>
              <div className="flex items-center gap-2">
                {quickCompetition && (
                  <Button onClick={() => setQuickOpen(true)} variant={timelessGames.length ? 'default' : 'outline'} className="flex items-center gap-2" data-tour="scheduler-quick-availability">
                    <CalendarDays className="h-4 w-4" />
                    {timelessGames.length ? `Set game times (${timelessGames.length})` : 'Set up availability'}
                  </Button>
                )}
                {hasUnsavedChanges && (
                  <>
                    <Button onClick={handleResetChanges} variant="outline" className="flex items-center gap-2">
                      Reset Changes
                    </Button>
                    <Button onClick={handleSaveChanges} className="flex items-center gap-2">
                      <Save className="h-4 w-4" />
                      Save Changes ({pendingGames.toCreate.length + pendingGames.toUpdate.length + pendingGames.toDelete.length})
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {timeSlots.length === 0 && displayGames.length === 0 ? (
              /* INT-0027 first-run: no slots AND no games. Primary action is
                 creating a game (division page quick-create, ?create=1 opens
                 the modal); TimeSlots are pitched as the upgrade, not a
                 prerequisite. With games but no slots the grid renders
                 (dates/times derive from the games themselves). */
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <CalendarDays className="mb-3 h-8 w-8 text-muted-foreground/40" />
                <p className="mb-1 font-medium">No games scheduled yet</p>
                <p className="text-muted-foreground mb-4 max-w-md text-sm">
                  Tell us when you play and the day grid turns on. Or create one game by hand: two teams, a date, a time.
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {quickCompetition && (
                    <Button onClick={() => setQuickOpen(true)} data-tour="scheduler-quick-availability-empty">
                      <CalendarDays className="mr-2 h-4 w-4" />
                      Set up availability
                    </Button>
                  )}
                  {divisions && divisions.length > 0 && (
                    <Button asChild variant="outline">
                      <Link to={`/admin/division/${divisions[0]._id}?create=1`}>
                        <Plus className="mr-2 h-4 w-4" />
                        Create a game by hand
                      </Link>
                    </Button>
                  )}
                </div>
                <p className="text-muted-foreground mt-4 text-xs max-w-md">
                  Prefer to manage venues yourself? {' '}
                  <Link to="/admin/locations" className="text-primary underline">Add court availability</Link>.
                </p>
              </div>
            ) : (
            <div className="overflow-auto">
              {/* Grid Layout */}
              <div className={`grid gap-1 min-w-max`} style={{ gridTemplateColumns: `120px repeat(${generateDates.length}, 250px)` }}>
                {/* Header row with dates */}
                <div className="sticky top-0 bg-muted p-2 text-center font-semibold border-b">
                  Time
                </div>
                {generateDates.map((date) => (
                  <div key={date} className="sticky top-0 bg-muted p-2 text-center font-semibold border-b">
                    <div className="text-sm">
                      {(() => {
                        let dateObj;
                        if (date.includes('-') && date.length === 10) {
                          const [month, day, year] = date.split('-');
                          dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                        } else {
                          dateObj = new Date(date);
                        }
                        return dateObj.toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric'
                        });
                      })()}
                    </div>
                  </div>
                ))}
                
                {/* Grid rows - each time slot */}
                {generateTimeSlots.map((timeSlot) => (
                  <React.Fragment key={timeSlot.time}>
                    {/* Time label */}
                    <div className="sticky left-0 bg-muted/50 p-2 text-sm font-medium border-r z-10">
                      {timeSlot.displayTime}
                    </div>
                    
                    {/* Grid cells for each date */}
                    {generateDates.map((date) => {
                      const gamesForCell = getGamesForTimeAndDateCallback(timeSlot.time, date);
                      const timeSlotsForCell = getTimeSlotsForTimeAndDateCallback(timeSlot.time, date);
                      
                      return (
                        <div key={`${timeSlot.time}-${date}`} className="min-h-[100px] border bg-card p-1 overflow-hidden">
                          {/* TimeSlots */}
                          {timeSlotsForCell.map((ts) => (
                            <div key={ts._id} className="mb-1">
                              {/* Only render the specific slot section that matches this grid time */}
                              {ts.slotSection && (
                                <SlotSection
                                  key={ts.slotSection.id}
                                  slotSection={ts.slotSection}
                                  teams={filteredTeams}
                                  timeSlot={ts}
                                  selectedDivisionIds={selectedDivisionIds}
                                  divisions={divisions}
                                  orgSport={organization?.sports?.[0]}
                                  teamsByDivision={teamsByDivision}
                                  activeId={activeId}
                                  tournamentId={tournamentId}
                                  leagueId={leagueId}
                                  isDragging={!!activeId}
                                  locationName={locations.find(l => l._id === ts.location._id)?.name || 'Unknown Location'}
                                  onGameUpdate={(game) => {
                                    setPendingGames(prev => {
                                      // Check if this game exists in toCreate (might be a placeholder game)
                                      const existingInCreate = prev.toCreate.find(g => g.slotSectionId === game.slotSectionId);
                                      
                                      if (existingInCreate) {
                                        // Update the existing game in toCreate
                                        return {
                                          ...prev,
                                          toCreate: prev.toCreate.map(g => 
                                            g.slotSectionId === game.slotSectionId ? game : g
                                          )
                                        };
                                      } else {
                                        // Update existing game in toUpdate
                                        return {
                                          ...prev,
                                          toUpdate: [...prev.toUpdate.filter(g => g._id !== game._id), game]
                                        };
                                      }
                                    });
                                    setHasUnsavedChanges(true);
                                  }}
                                  onGameCreate={(game) => {
                                    setPendingGames(prev => {
                                      // Check if a game with this slotSectionId already exists
                                      const existingGame = prev.toCreate.find(g => g.slotSectionId === game.slotSectionId);
                                      
                                      if (existingGame) {
                                        // Update the existing game instead of creating a new one
                                        return {
                                          ...prev,
                                          toCreate: prev.toCreate.map(g => 
                                            g.slotSectionId === game.slotSectionId ? game : g
                                          )
                                        };
                                      } else {
                                        // Create new game
                                        return {
                                          ...prev,
                                          toCreate: [...prev.toCreate, game]
                                        };
                                      }
                                    });
                                    setHasUnsavedChanges(true);
                                  }}
                                  onGameDelete={(gameId) => {
                                    setPendingGames(prev => {
                                      if (gameId.startsWith('temp-')) {
                                        const slotSectionId = gameId.replace('temp-create-', '');
                                        const filteredToCreate = prev.toCreate.filter(game => {
                                          return game.slotSectionId !== slotSectionId;
                                        });
                                        const filteredToUpdate = prev.toUpdate.filter(game => {
                                          return game._id !== gameId;
                                        });
                                        return {
                                          ...prev,
                                          toCreate: filteredToCreate,
                                          toUpdate: filteredToUpdate
                                        };
                                      }
                                      return {
                                        ...prev,
                                        toDelete: [...prev.toDelete, gameId]
                                      };
                                    });
                                    setHasUnsavedChanges(true);
                                  }}
                                  formatTimeForDisplay={formatTimeForDisplay}
                                />
                              )}
                            </div>
                          ))}
                          
                          {/* Games without TimeSlots */}
                          {(() => {
                            // Get all games that are already being displayed in SlotSections for this cell
                            const gamesInSlotSections = new Set<string>();
                            timeSlotsForCell.forEach(ts => {
                              if (ts.slotSection && ts.slotSection.game && ts.slotSection.game._id) {
                                gamesInSlotSections.add(ts.slotSection.game._id);
                              }
                            });
                            
                            // Only show games that don't have a timeSlot AND are not already in SlotSections
                            return gamesForCell.filter(game => 
                              !game.timeSlot && !gamesInSlotSections.has(game._id)
                            ).map((game) => (
                              <GameSlot
                                key={game._id}
                                game={game as any}
                                divisions={divisions}
                                formatTimeForDisplay={formatTimeForDisplay}
                                variant="withoutTimeSlot"
                              />
                            ));
                          })()}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
            )}
          </CardContent>
        </Card>
        
        <DragOverlay
          // Optimize drag overlay for better performance
          dropAnimation={{
            duration: 200,
            easing: 'ease-out',
          }}
          style={{ maxWidth: '200px' }}
        >
          {activeTeam ? <DraggableTeam team={activeTeam} isDragOverlay /> : null}
        </DragOverlay>
      </DndContext>
    </PageContainer>
  );
}
